import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { VatRefund } from '../../../types';
import { useState, useEffect } from 'react';

const COLLECTION_NAME = 'vatRefunds';

const toVatRefund = (d: any): VatRefund => {
  const data = d.data();
  return {
    id: d.id,
    amount: data.amount || 0,
    date: data.date?.toDate() || new Date(),
    receiptUrl: data.receiptUrl || '',
    receiptName: data.receiptName || '',
    notes: data.notes || '',
    status: data.status || 'pending',
    createdBy: data.createdBy || '',
    createdByName: data.createdByName || '',
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate(),
  };
};

export const subscribeVatRefunds = (callback: (items: VatRefund[]) => void) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(toVatRefund));
  });
};

export const addVatRefund = async (
  entry: Omit<VatRefund, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const data: any = {
    ...entry,
    date: entry.date instanceof Date ? Timestamp.fromDate(entry.date) : entry.date,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  const docRef = await addDoc(collection(db, COLLECTION_NAME), data);
  return docRef.id;
};

export const updateVatRefund = async (
  id: string,
  updates: Partial<Omit<VatRefund, 'id' | 'createdAt'>>
): Promise<void> => {
  const data: any = { ...updates, updatedAt: Timestamp.now() };
  if (data.date instanceof Date) data.date = Timestamp.fromDate(data.date);
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  await updateDoc(doc(db, COLLECTION_NAME, id), data);
};

export const deleteVatRefund = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
};

export const useVatRefunds = () => {
  const [vatRefunds, setVatRefunds] = useState<VatRefund[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeVatRefunds((items) => {
      setVatRefunds(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return { vatRefunds, loading };
};
